import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarCheck } from 'lucide-react';
import { useOwnerReport } from '../hooks/useOwnerReport';
import { buildReportMessage, weekLabel } from '../utils/ownerReport';
import { reportStatusInfo } from '../utils/ownerReportPage';
import { useBusinessStore } from '../store/useBusinessStore';

const card = 'bg-seryi-bg border border-seryi-bg-light rounded-3xl p-5 space-y-3';

export default function OwnerReportPage() {
  const { enabled, notifyPhone, history, preview, previewing, loading, missing, toggle, saveNotifyPhone, showExample, closeExample } = useOwnerReport();
  const { masterDb, activeBusinessId } = useBusinessStore();
  const [phone, setPhone] = useState('');
  useEffect(() => { setPhone(notifyPhone || ''); }, [notifyPhone]);

  if (missing) {
    return <div className="p-6 text-xs text-amber-300">Falta ejecutar la migración <code>20261016a_owner_weekly_report.sql</code> en Supabase para usar el reporte semanal.</div>;
  }

  const business = (Array.isArray(masterDb) ? masterDb : []).find((b) => String(b.id) === String(activeBusinessId));
  const name = business?.nombre_negocio || '';

  return (
    <div className="space-y-5 max-w-4xl">
      <div>
        <h2 className="text-xl font-extrabold text-seryi-heading flex items-center gap-2"><CalendarCheck size={20} /> Reporte semanal</h2>
        <p className="text-xs text-seryi-text">Cada lunes a las 8 am te llega por WhatsApp el resumen de la semana: conversaciones, clientes nuevos, ventas, cobros, gastos, lo que te deben y las cotizaciones, comparado con la semana anterior, más 2 o 3 sugerencias escritas por la IA a partir de esas cifras. Los números salen de lo que ya registra Keyla (pedidos, citas, ventas y gastos).</p>
      </div>

      {loading ? <p className="text-xs text-seryi-text">Cargando…</p> : (
        <>
          <section aria-label="Ajustes" className={card}>
            <label className="flex items-center gap-2 text-xs font-bold text-seryi-heading">
              <input type="checkbox" checked={enabled} onChange={(e) => toggle(e.target.checked)} />
              Enviarme el resumen cada lunes
            </label>
            <form onSubmit={(e) => { e.preventDefault(); saveNotifyPhone(phone); }} className="space-y-1">
              <label htmlFor="or-phone" className="block text-[11px] text-seryi-text">WhatsApp donde te llega (el mismo de los avisos de cotizaciones y de las preguntas del bot)</label>
              <div className="flex gap-2">
                <input id="or-phone" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="573016867636" className="w-full max-w-xs bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading" />
                <button type="submit" className="bg-seryi-green text-seryi-bg-dark text-xs font-extrabold px-4 py-2 rounded-xl">Guardar</button>
              </div>
              {enabled && !notifyPhone && <p role="alert" className="text-[11px] text-amber-300">Sin número de WhatsApp no se puede enviar el reporte.</p>}
              <p className="text-[11px] text-seryi-text">Usa tu celular personal, no el WhatsApp del negocio. Si no hubo ninguna actividad en la semana, no se envía nada.</p>
            </form>
          </section>

          <section aria-label="Ejemplo" className={card}>
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xs font-extrabold uppercase tracking-wider text-seryi-heading">Ejemplo con la última semana</h3>
              <button type="button" disabled={previewing} onClick={showExample} className="border border-seryi-bg-light text-seryi-heading text-xs font-bold px-3 py-1.5 rounded-xl disabled:opacity-50">{previewing ? 'Armando…' : 'Ver ejemplo'}</button>
            </div>
            {preview ? (
              <div className="space-y-2">
                <pre aria-label="Texto del ejemplo" className="whitespace-pre-wrap text-xs text-seryi-heading bg-seryi-bg-dark border border-seryi-bg-light rounded-2xl p-4 font-sans">{buildReportMessage(preview, name)}</pre>
                <p className="text-[11px] text-seryi-text">Así llegan las cifras. Las sugerencias las escribe la IA al enviarlo y no aparecen en este ejemplo.</p>
                <button type="button" onClick={closeExample} className="text-[11px] text-seryi-text underline">Ocultar</button>
              </div>
            ) : <p className="text-xs text-seryi-text">Mira cómo se vería el resumen de la última semana completa, sin enviarlo.</p>}
          </section>

          <section aria-label="Historial" className={card}>
            <h3 className="text-xs font-extrabold uppercase tracking-wider text-seryi-heading">Enviados</h3>
            {history.length === 0 ? <p className="text-xs text-seryi-text">Aún no se ha enviado ninguno. El primero sale el próximo lunes si el reporte está encendido.</p> : history.map((r) => {
              const info = reportStatusInfo(r);
              return (
                <details key={r.id} className="border-t border-seryi-bg-light pt-2 text-xs">
                  <summary className="cursor-pointer flex flex-wrap items-center justify-between gap-2 text-seryi-heading">
                    <span className="font-bold">{weekLabel(r.week_start, new Date(Date.parse(`${r.week_end}T00:00:00Z`) + 86400000).toISOString().slice(0, 10))}</span>
                    <span className={info.tone}>{info.label}</span>
                  </summary>
                  {r.status === 'error' && r.error && <p className="text-rose-300 mt-1">{r.error}</p>}
                  <pre className="whitespace-pre-wrap text-seryi-text font-sans mt-2">{r.message || (r.data ? buildReportMessage(r.data, name) : '')}</pre>
                </details>
              );
            })}
          </section>
          <p className="text-[11px] text-seryi-text">Las preguntas que el bot no supo contestar se responden en <Link to="/v2/preguntas-bot" className="text-seryi-green hover:underline">Preguntas del bot</Link>.</p>
        </>
      )}
    </div>
  );
}
